import { useState, type ReactNode } from "react";
import type { ActionCard as ActionCardType, ActionCardWrite } from "../types/cards";

interface Props {
  card: ActionCardType;
  onApply: (lineIds: string[]) => Promise<unknown>;
  onRetry: (lineId: string) => Promise<unknown>;
  onDismiss: () => Promise<unknown>;
  // Briefly highlights the card when opened from "Needs your attention".
  highlighted?: boolean;
}

// One chat-generated plan. Pending cards carry a maroon accent and a
// checkbox per plan line (line-level opt-out); the plan lines ARE the
// confirmation — there is no separate dialog.
//
// v1.1: a line can group several per-person MSP writes (sub requests
// group per Mass — "Open sub requests — 7:30 AM EMHC, Jul 5 (Bob,
// Jane)"). Applying a grouped line issues each person's write
// independently; the line status rolls up (any failure → ✕ + Retry),
// and Retry re-runs only the failed individuals.
export default function ActionCard({
  card,
  onApply,
  onRetry,
  onDismiss,
  highlighted,
}: Props) {
  // Line-level opt-out. Default: everything checked.
  const [unchecked, setUnchecked] = useState<Set<string>>(new Set());
  // Line ids currently in flight (apply or retry).
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [dismissing, setDismissing] = useState(false);
  // Receipts collapse by default; chevron expands back to line detail.
  const [expanded, setExpanded] = useState(false);
  // Network/API failure on apply/retry/dismiss — distinct from a
  // line's failed status, which is an MSP write failure.
  const [requestError, setRequestError] = useState<string | null>(null);

  const isReceipt = card.status === "applied";
  const pendingLines = card.lines.filter((l) => l.status === "pending");
  const checkedIds = pendingLines.filter((l) => !unchecked.has(l.id)).map((l) => l.id);
  const appliedCount = card.lines.filter((l) => l.status === "applied").length;
  const hasResults = card.lines.some((l) => l.status !== "pending");

  const toggleLine = (id: string) => {
    setUnchecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const apply = async () => {
    if (checkedIds.length === 0) return;
    setBusy(new Set(checkedIds));
    setRequestError(null);
    try {
      await onApply(checkedIds);
    } catch (err) {
      setRequestError((err as Error).message);
    } finally {
      setBusy(new Set());
    }
  };

  const retry = async (lineId: string) => {
    setBusy(new Set([lineId]));
    setRequestError(null);
    try {
      await onRetry(lineId);
    } catch (err) {
      setRequestError((err as Error).message);
    } finally {
      setBusy(new Set());
    }
  };

  const dismiss = async () => {
    setDismissing(true);
    setRequestError(null);
    try {
      await onDismiss();
    } catch (err) {
      setRequestError((err as Error).message);
    } finally {
      setDismissing(false);
    }
  };

  if (isReceipt && !expanded) {
    return (
      <article
        className="action-card action-card--applied action-card--receipt"
        aria-label={card.title}
      >
        <button
          type="button"
          className="action-card__receipt-row"
          onClick={() => setExpanded(true)}
          aria-expanded={false}
        >
          <span className="action-card__check" aria-hidden="true">✓</span>
          <span className="action-card__receipt-title">{card.title}</span>
          <span className="action-card__receipt-count">
            — {appliedCount} action{appliedCount === 1 ? "" : "s"} applied
          </span>
          <span className="action-card__receipt-date">{shortDate(card.appliedAt)}</span>
          <span className="action-card__chevron" aria-hidden="true">›</span>
        </button>
      </article>
    );
  }

  return (
    <article
      className={`action-card${isReceipt ? " action-card--applied" : ""}${highlighted ? " action-card--highlight" : ""}`}
      aria-label={card.title}
    >
      <header className="action-card__header">
        <div>
          <h3 className="action-card__title">{card.title}</h3>
          <p className="action-card__meta">
            {isReceipt
              ? `applied ${relativeTime(card.appliedAt)}`
              : /* Everything in this panel comes from chat — the "from
                   chat" label was redundant; just the relative time. */
                relativeTime(card.createdAt)}
          </p>
        </div>
        {isReceipt ? (
          <button
            type="button"
            className="action-card__collapse"
            onClick={() => setExpanded(false)}
            aria-label="Collapse to receipt"
          >
            ›
          </button>
        ) : (
          // Active cards get a top-right dismiss × (matches the Needs
          // Your Attention pattern). Permanent server-side dismiss; the
          // AI never re-raises it. Receipts are history — no ×.
          <button
            type="button"
            className="action-card__dismiss-x"
            onClick={() => void dismiss()}
            disabled={dismissing || busy.size > 0}
            aria-label={`Dismiss ${card.title}`}
            title="Dismiss"
          >
            ×
          </button>
        )}
      </header>

      {card.contextSentence ? (
        <p className="action-card__context">{card.contextSentence}</p>
      ) : null}

      <ul className="action-card__lines">
        {card.lines.map((line) => {
          const isBusy = busy.has(line.id);
          const grouped = line.writes.length > 1;
          const showPeople = grouped && (line.status !== "pending" || isBusy);
          return (
            <li key={line.id} className={`action-card__line action-card__line--${line.status}`}>
              <span className="action-card__line-status">
                {isBusy ? (
                  <span className="action-card__line-spinner" aria-label="Applying" />
                ) : line.status === "applied" ? (
                  <span className="action-card__line-ok" aria-label="Applied">✓</span>
                ) : line.status === "failed" ? (
                  <span className="action-card__line-fail" aria-label="Failed">✕</span>
                ) : line.status === "skipped" ? (
                  <span className="action-card__line-skip" aria-label="Skipped">–</span>
                ) : (
                  <input
                    type="checkbox"
                    className="action-card__line-checkbox"
                    checked={!unchecked.has(line.id)}
                    onChange={() => toggleLine(line.id)}
                    aria-label={`Include: ${stripBold(line.description)}`}
                  />
                )}
              </span>
              <span className="action-card__line-body">
                <span className="action-card__line-desc">{renderBold(line.description)}</span>
                {showPeople ? (
                  <span className="action-card__people">
                    {line.writes.map((w, i) => (
                      <PersonPill key={i} write={w} busy={isBusy} />
                    ))}
                  </span>
                ) : null}
                {line.status === "failed" && line.error ? (
                  <span className="action-card__line-error">{line.error}</span>
                ) : null}
                {line.status === "applied" ? (
                  <MspCalls writes={line.writes} />
                ) : null}
              </span>
              {line.status === "failed" && !isBusy ? (
                <button
                  type="button"
                  className="action-card__retry"
                  onClick={() => void retry(line.id)}
                >
                  Retry
                </button>
              ) : null}
              <span className="action-card__line-type">{line.actionType}</span>
            </li>
          );
        })}
      </ul>

      {requestError ? (
        <p className="action-card__request-error">{requestError}</p>
      ) : null}

      {!isReceipt ? (
        <footer className="action-card__footer">
          <button
            type="button"
            className="action-card__apply"
            disabled={checkedIds.length === 0 || busy.size > 0}
            onClick={() => void apply()}
          >
            {busy.size > 0
              ? "Applying…"
              : checkedIds.length === 0
                ? hasResults
                  ? "Nothing left to apply"
                  : "Nothing selected"
                : `Apply all (${checkedIds.length})`}
          </button>
          <span className="action-card__hint">
            To change dates or Masses, just reply in chat
          </span>
        </footer>
      ) : null}
    </article>
  );
}

// Per-person status chip inside a grouped line: name + ✓/✕/spinner.
function PersonPill({ write, busy }: { write: ActionCardWrite; busy: boolean }) {
  const icon = busy && write.status === "pending" ? "spinner" : write.status;
  return (
    <span className={`action-card__person action-card__person--${write.status}`}>
      {icon === "spinner" ? (
        <span className="action-card__person-spinner" aria-hidden="true" />
      ) : write.status === "applied" ? (
        <span aria-hidden="true">✓</span>
      ) : write.status === "failed" ? (
        <span aria-hidden="true">✕</span>
      ) : write.status === "skipped" ? (
        <span aria-hidden="true">–</span>
      ) : null}
      {write.label}
    </span>
  );
}

// Expandable "what hit MSP" — one row per applied person's request.
function MspCalls({ writes }: { writes: ActionCardWrite[] }) {
  const calls = writes.filter((w) => w.mockRequest);
  if (calls.length === 0) return null;
  return (
    <details className="action-card__line-msp">
      <summary>MSP {calls.length === 1 ? "call" : "calls"}</summary>
      {calls.map((w, i) => (
        <code key={i} className="action-card__msp-row">
          {w.label}: {w.mockRequest!.method} {w.mockRequest!.path}
        </code>
      ))}
    </details>
  );
}

// "Mark **Bob Smith** unavailable" → bolds the entity. Plain split —
// the model only emits paired ** markers.
function renderBold(text: string): ReactNode[] {
  return text
    .split("**")
    .map((part, i) =>
      i % 2 === 1 ? <strong key={i}>{part}</strong> : <span key={i}>{part}</span>,
    );
}

function stripBold(text: string): string {
  return text.replaceAll("**", "");
}

function relativeTime(iso: string | null): string {
  if (!iso) return "";
  const ms = Date.now() - Date.parse(iso);
  const minutes = Math.round(ms / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return shortDate(iso);
}

function shortDate(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}
